import { FC, useMemo } from 'react';
import ItemImageButton from '../../components/ItemImageButton/ItemImageButton';
import { IItemImagesProps } from '../../types';
import { RESTRICTION_ACCESS } from 'src/constants/integrations';
import { localeStore } from 'src/mobx/localesStore';
import { arrayCompare } from 'src/utils/helpers';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import './style.scss';

type TProps = Pick<IItemImagesProps, 'images' | 'currentSaveImage' | 'currentSaveImages' | 'disabled'>;

interface IProps extends TProps {
  addMode: boolean;
  editMode: boolean;
  imageValid: boolean;
  imagesRestrictionAccess: RESTRICTION_ACCESS | undefined;
  onChangeEditMode: () => void;
  onImagesChangesSubmit: () => void;
}

const ItemImagesHeader: FC<IProps> = ({ addMode, editMode, imageValid, disabled, ...props }: IProps): JSX.Element | null => {
  // whether to show a block with requirements
  const imagesRequirements = useMemo((): boolean => Boolean(imageValid && (props.images?.length || addMode)), [imageValid, props.images, addMode]);
  // whether to show a block with controls
  const imagesControlBlock = useMemo((): boolean => {
    return Boolean(props.images?.length || props.currentSaveImages?.length || props.currentSaveImage);
  }, [props.images, props.currentSaveImage, props.currentSaveImages]);
  //  whether to show reset images button. Checks if there have been changes in array of images
  const resetButton = useMemo((): boolean => {
    return !arrayCompare([props.currentSaveImage, ...(props.currentSaveImages || [])], props.images || [], true);
  }, [props.images, props.images?.length, props.currentSaveImage, props.currentSaveImages]);

  if (!imagesRequirements && !imagesControlBlock) return null;

  return (
    <div className={`item-images-header mb-2 justify-content-${imagesRequirements ? 'between' : 'end'}`}>
      {/* requirements string */}
      {imagesRequirements ? <p className="item-images-header__requirements">{localeStore.t('item_img_requirements')}</p> : null}
      {/* controls block */}
      {imagesControlBlock ? (
        <div className="d-flex flex-row gap-10">
          {resetButton ? <ItemImageButton title={localeStore.t('reset')} onClick={props.onImagesChangesSubmit} disabled={disabled} /> : null}
          <IntegrationManager setting={props.imagesRestrictionAccess} withTooltip={!disabled}>
            {!props.images?.length ? null : (
              <ItemImageButton
                title={localeStore.t(editMode ? 'Done' : 'edit')}
                type={editMode ? 'primary' : undefined}
                onClick={props.onChangeEditMode}
                disabled={disabled}
              />
            )}
          </IntegrationManager>
        </div>
      ) : null}
    </div>
  );
};

export default ItemImagesHeader;
